import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { EditorHeader } from "./EditorHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Trash2 } from "lucide-react";

export const RisksEditor = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [newRisk, setNewRisk] = useState({
    risk: "",
    description: "",
    severity: "medium",
    mitigation: "",
  });

  const { data: risks, isLoading, refetch } = useQuery({
    queryKey: ['risks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('risk_items')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch risks data",
        });
        throw error;
      }

      return data;
    },
  });

  const handleAddRisk = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('risk_items')
        .insert([newRisk]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Risk added successfully",
      });

      setNewRisk({
        risk: "",
        description: "",
        severity: "medium",
        mitigation: "",
      });

      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add risk",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRisk = async (id: string) => {
    try {
      const { error } = await supabase
        .from('risk_items')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Risk deleted successfully",
      });

      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete risk",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <EditorHeader title="Edit Risks" />
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <EditorHeader title="Edit Risks" />
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleAddRisk} className="space-y-4">
            <div className="grid gap-4">
              <div>
                <Input
                  placeholder="Risk Title"
                  value={newRisk.risk}
                  onChange={(e) => setNewRisk({ ...newRisk, risk: e.target.value })}
                />
              </div>
              <div>
                <Textarea
                  placeholder="Risk Description"
                  value={newRisk.description}
                  onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })}
                />
              </div>
              <div>
                <Select
                  value={newRisk.severity}
                  onValueChange={(value) => setNewRisk({ ...newRisk, severity: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Textarea
                  placeholder="Mitigation Strategy"
                  value={newRisk.mitigation}
                  onChange={(e) => setNewRisk({ ...newRisk, mitigation: e.target.value })}
                />
              </div>
            </div>
            <Button type="submit" disabled={loading}>
              Add Risk
            </Button>
          </form>

          <div className="mt-8 space-y-4">
            {risks?.map((risk) => (
              <Card key={risk.id} className="p-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      <h3 className="font-medium">{risk.risk}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{risk.description}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Severity:</span>
                      <span className="text-sm text-muted-foreground capitalize">{risk.severity}</span>
                    </div>
                    <p className="text-sm">
                      <span className="font-medium">Mitigation:</span>{" "}
                      {risk.mitigation}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteRisk(risk.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};